import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllOrganizations } from "../../utils/Api";
import LoadingPage from "../../components/LoadingPage";
import {
  Building2,
  ArrowUp,
  ArrowDown,
  Users,
  Plus,
  Search,
  FileText,
  Files,
} from "lucide-react";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import CreateOrganizationModal from "../../modals/CreateOrganization";

interface Organization {
  id: string;
  name: string;
  member_count: number;
  document_count: number;
  total_pages: number;
  created_at: string;
}

function Organizations() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [displayedCount, setDisplayedCount] = useState(10);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const fetchOrganizations = async () => {
    try {
      const response = await getAllOrganizations();
      setOrganizations(response.data);
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const sortedOrganizations = [...filteredOrganizations].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  const totalOrganizations = sortedOrganizations.length;
  const displayedOrganizations = sortedOrganizations.slice(0, displayedCount);
  const remainingCount = totalOrganizations - displayedCount;

  const handleShowMore = () => {
    setDisplayedCount((prev) => prev + 10);
  };

  const handleShowAll = () => {
    setDisplayedCount(totalOrganizations);
  };

  if (loading) {
    return (
      <LoadingPage
        fullScreen={false}
        variant="light"
        message="Loading organizations"
      />
    );
  }

  if (error) {
    return (
      <div className="bg-gray-100 min-h-full">
        <div className="w-full mx-auto py-6 sm:py-12 px-4 sm:px-6 lg:px-10">
          <div className="mb-4">
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm sm:text-base"
              role="alert"
            >
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-6 sm:pt-12 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-0 mb-4 sm:mb-6">
          <div>
            <h3 className="text-xl sm:text-2xl font-semibold">Organisations</h3>
            <p className="m-0 text-sm sm:text-base text-gray-500">
              View and manage all organisations
            </p>
          </div>
          <PrimaryButton
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            <Plus size={16} />
            <span>Create organisation</span>
          </PrimaryButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pt-4 sm:pt-6 pb-8 sm:pb-12">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h6 className="text-sm sm:text-base font-semibold">
              All organisations
            </h6>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className="w-full sm:max-w-md pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent"
              placeholder="Search organisations..."
              aria-label="Search organisations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="bg-white rounded shadow">
            <table className="w-full">
              <thead className="bg-white border-b border-gray-200">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600">
                    Name
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden sm:table-cell">
                    Members
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden sm:table-cell">
                    Documents
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden sm:table-cell">
                    Pages
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden md:table-cell">
                    <button
                      onClick={toggleSortOrder}
                      className="flex items-center gap-2 text-gray-600 transition-colors"
                      title={`Sort by date ${
                        sortOrder === "asc" ? "descending" : "ascending"
                      }`}
                      disabled={filteredOrganizations.length === 0}
                    >
                      Created at
                      {sortOrder === "desc" ? (
                        <ArrowDown size={16} />
                      ) : (
                        <ArrowUp size={16} />
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrganizations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 sm:px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        {searchQuery ? (
                          <>
                            <h4 className="text-lg font-semibold mb-2">
                              No organisations found
                            </h4>
                            <p>
                              No organisations match your search "{searchQuery}"
                            </p>
                          </>
                        ) : (
                          <>
                            <h4 className="text-lg font-semibold mb-2">
                              No organizations yet
                            </h4>
                            <p>Organisations will appear here once created</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  displayedOrganizations.map((org) => (
                    <tr
                      key={org.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-[#1d365d] hover:text-[#2d4a75]">
                              {org.name}
                            </div>
                            <div className="text-xs text-gray-500 sm:hidden flex items-center gap-1">
                              <Users size={12} />
                              {org.member_count} member
                              {org.member_count !== 1 ? "s" : ""}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden sm:table-cell">
                        <span className="flex items-center gap-2">
                          {org.member_count}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden sm:table-cell">
                        <span className="flex items-center gap-2">
                          {org.document_count}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden sm:table-cell">
                        <span className="flex items-center gap-2">
                          {org.total_pages}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden md:table-cell">
                        {org.created_at
                          ? new Date(org.created_at).toLocaleString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : "Unknown"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {filteredOrganizations.length > 0 && (
              <div className="px-3 sm:px-6 py-4 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  {searchQuery ? (
                    <>
                      Showing{" "}
                      <span className="font-medium">
                        {filteredOrganizations.length}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {organizations.length}
                      </span>{" "}
                      organisations
                    </>
                  ) : (
                    <>
                      Total organisations:{" "}
                      <span className="font-medium">
                        {organizations.length}
                      </span>
                    </>
                  )}
                </p>
              </div>
            )}
          </div>

          {remainingCount > 0 && (
            <div className="mt-4 sm:mt-6 text-center">
              <SecondaryButton
                onClick={handleShowMore}
                className="mb-3 w-full sm:w-auto"
              >
                Show {Math.min(remainingCount, 10)} more organisations
              </SecondaryButton>
              <p className="text-sm text-gray-600">
                Showing {displayedCount} of {totalOrganizations}.{" "}
                <button
                  onClick={handleShowAll}
                  className="text-[#1d365d] hover:text-[#2d4a75] font-medium underline"
                >
                  Show all
                </button>
              </p>
            </div>
          )}
        </div>
      </div>

      <CreateOrganizationModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        onSuccess={fetchOrganizations}
      />
    </div>
  );
}

export default Organizations;
